import { useCallback, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { usePipelineList } from '../../hooks/usePipelines.js';
import { PipelineSkeleton } from '../../components/feedback/PipelineSkeleton/index.js';
import { ErrorState } from '../../components/feedback/ErrorState/index.js';
import { PipelineListTable } from '../../components/features/pipeline/PipelineListTable.js';
import { PipelineDetailPanel } from '../../components/features/pipeline/PipelineDetailPanel.js';
import {
  PipelineFilterBar,
  type PipelineFilters,
} from '../../components/features/pipeline/PipelineFilterBar.js';
import { PipelineTriggerButton } from '../../components/features/pipeline/PipelineTriggerButton.js';
import { useToast } from '../../components/ui/Toast/index.js';
import { matchesSearch, pipelineSearchSchema } from '../../lib/pipeline-filter.js';
import type { PipelineConfig } from '../../types/api.js';

/**
 * @graph-node {Page}
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business パイプライン一覧ページ。フィルターバー・テーブル・詳細パネル・手動実行ボタンを統合表示。フィルタ状態は URL search params に persist し、リロード / 共有 URL で復元される
 * @graph-connects usePipelineList [calls] パイプライン一覧取得フック呼び出し
 * @graph-connects PipelineListTable [calls] パイプラインテーブルコンポーネント描画
 * @graph-connects PipelineDetailPanel [calls] パイプライン詳細パネルコンポーネント描画
 * @graph-connects PipelineFilterBar [calls] フィルターバーコンポーネント描画
 * @graph-connects PipelineTriggerButton [calls] 手動実行ボタンコンポーネント描画
 * @graph-connects PipelineSkeleton [calls] スケルトンローディング描画
 * @graph-connects ErrorState [calls] エラー状態描画
 */
export const Route = createFileRoute('/pipelines/')({
  validateSearch: pipelineSearchSchema,
  component: PipelinesIndexPage,
});

/** @graph-connects none */
function PipelinesIndexPage() {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = usePipelineList();
  const { showToast } = useToast();

  const filters = Route.useSearch();
  const navigate = Route.useNavigate();

  const updateFilters = useCallback(
    (next: PipelineFilters) => {
      void navigate({ search: () => next, replace: true });
    },
    [navigate],
  );

  const [selectedConfig, setSelectedConfig] = useState<PipelineConfig | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [triggerTarget, setTriggerTarget] = useState<PipelineConfig | null>(null);

  const configs = useMemo(() => data?.configs ?? [], [data]);
  const filteredConfigs = useMemo(
    () =>
      configs.filter((config) => {
        if (filters.type !== 'all' && config.type !== filters.type) return false;
        if (filters.status !== 'all' && config.status !== filters.status) return false;
        if (!matchesSearch(config, filters.search)) return false;
        return true;
      }),
    [configs, filters],
  );

  const handleSelect = (config: PipelineConfig) => {
    setSelectedConfig(config);
    setIsDetailOpen(true);
  };

  const handleTrigger = (config: PipelineConfig) => {
    setTriggerTarget(config);
  };

  const handleTriggerSuccess = () => {
    showToast('success', t('pipeline.triggerSuccess'), 3000);
    setTriggerTarget(null);
  };

  const handleTriggerError = () => {
    showToast('error', t('pipeline.triggerError'), 5000);
    setTriggerTarget(null);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ページヘッダー */}
      <div className="animate-content-reveal">
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('pipeline.title')}
        </h1>
      </div>

      {/* フィルターバー */}
      <PipelineFilterBar
        filters={filters}
        onFilterChange={updateFilters}
        resultCount={filteredConfigs.length}
        totalCount={configs.length}
      />

      {/* コンテンツ */}
      {isLoading ? (
        <PipelineSkeleton />
      ) : error ? (
        <ErrorState
          message={t('common.error')}
          onRetry={() => {
            void refetch();
          }}
        />
      ) : filteredConfigs.length === 0 ? (
        <div className="empty-state animate-fade-in">
          <p className="text-[var(--color-text-muted)]">{t('pipeline.noConfigs')}</p>
        </div>
      ) : (
        <PipelineListTable
          configs={filteredConfigs}
          onSelect={handleSelect}
          onTrigger={handleTrigger}
        />
      )}

      {/* 詳細パネル */}
      <PipelineDetailPanel
        config={selectedConfig}
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
        }}
      />

      {/* トリガー確認 */}
      {triggerTarget && (
        <PipelineTriggerButton
          config={triggerTarget}
          onSuccess={handleTriggerSuccess}
          onError={handleTriggerError}
          onCancel={() => {
            setTriggerTarget(null);
          }}
        />
      )}
    </div>
  );
}
